import React, { useState, useRef } from 'react';
import { Upload, X, Image, Loader2, Check, Camera, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Image size guidelines
const SIZE_GUIDELINES = {
  hero: { width: 1920, height: 800, maxSize: '500KB' },
  product: { width: 800, height: 800, maxSize: '200KB' },
  thumbnail: { width: 400, height: 400, maxSize: '100KB' },
  team: { width: 400, height: 400, maxSize: '150KB' },
  service: { width: 600, height: 400, maxSize: '200KB' },
  icon: { width: 128, height: 128, maxSize: '50KB' },
  logo: { width: 200, height: 60, maxSize: '100KB' },
  favicon: { width: 32, height: 32, maxSize: '20KB' },
  gallery: { width: 1200, height: 800, maxSize: '300KB' },
  blog: { width: 1200, height: 630, maxSize: '300KB' },
  video_thumbnail: { width: 1080, height: 1920, maxSize: '500KB' },
  general: { width: 800, height: 600, maxSize: '300KB' }
};

function ImageUploader({ 
  value, 
  onChange, 
  folder = 'general',
  type = 'general',
  label = 'Image',
  showUrlInput = true,
  className = ''
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showUrlField, setShowUrlField] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const fileInputRef = useRef(null);

  const guideline = SIZE_GUIDELINES[type] || SIZE_GUIDELINES.general;

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, WebP, or SVG.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);
      formData.append('alt_text', label);

      const res = await fetch(`${BACKEND_URL}/api/upload`, {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        // Ensure HTTPS
        let imageUrl = data.url;
        if (imageUrl && imageUrl.startsWith('http://')) {
          imageUrl = imageUrl.replace('http://', 'https://');
        }
        onChange(imageUrl);
        toast.success('Image uploaded successfully!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleRemove = () => {
    onChange('');
    setUrlValue('');
    setShowUrlField(false);
  };

  const handleUrlSubmit = () => {
    if (urlValue && urlValue.trim()) {
      onChange(urlValue.trim());
      setShowUrlField(false);
      toast.success('Image URL added');
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
        <span className="text-xs text-gray-400 ml-2">
          (Recommended: {guideline.width}x{guideline.height}px)
        </span>
      </label>

      {/* Preview Mode - Show when image exists */}
      {value ? (
        <div className="relative group rounded-xl overflow-hidden border-2 border-gray-200">
          <img 
            src={value} 
            alt={label}
            className="w-full h-48 object-cover"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center pb-4 gap-3">
            <button
              type="button"
              onClick={triggerFileInput}
              className="px-4 py-2 bg-white text-gray-900 rounded-lg text-sm font-medium hover:bg-gray-100 shadow-lg flex items-center gap-2"
            >
              <Camera size={16} />
              Change
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 shadow-lg flex items-center gap-2"
            >
              <X size={16} />
              Remove
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files[0])}
            className="hidden"
          />
        </div>
      ) : (
        /* Upload Mode - Show when no image */
        <div className="space-y-3">
          {/* Main Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`relative border-2 border-dashed rounded-xl transition-all duration-300 ${
              dragOver 
                ? 'border-blue-500 bg-blue-50 scale-[1.02]' 
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              className="hidden"
            />
            
            {uploading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="relative">
                  <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-6 bg-blue-100 rounded-full"></div>
                  </div>
                </div>
                <p className="text-sm font-medium text-blue-600">Uploading your image...</p>
                <p className="text-xs text-gray-500">Please wait</p>
              </div>
            ) : (
              <div className="py-8 px-4">
                {/* Upload Button - Primary Action */}
                <div className="flex flex-col items-center gap-4">
                  <button
                    type="button"
                    onClick={triggerFileInput}
                    className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-105 transition-all duration-300"
                  >
                    <Upload size={22} />
                    <span>Upload from Device</span>
                  </button>
                  
                  <p className="text-sm text-gray-500">
                    or drag and drop your image here
                  </p>
                  
                  <p className="text-xs text-gray-400">
                    Supports: JPG, PNG, GIF, WebP • Max 5MB
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* URL Input Option */}
          {showUrlInput && (
            <div className="space-y-2">
              {!showUrlField ? (
                <button
                  type="button"
                  onClick={() => setShowUrlField(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors text-sm"
                >
                  <Link2 size={16} />
                  <span>Or paste image URL</span>
                </button>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={urlValue}
                    onChange={(e) => setUrlValue(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleUrlSubmit();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleUrlSubmit}
                    disabled={!urlValue.trim()}
                    className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Check size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUrlField(false);
                      setUrlValue('');
                    }}
                    className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ImageUploader;
